import { useEffect, useId, useRef, type ReactNode } from "react";
import { t } from "../i18n";
import { Icon } from "./Icon";

/**
 * Шторка снизу (на широком экране — окно по центру): таймер сна, подарок герою.
 * Закрывается фоном, Esc и крестиком; фокус уходит внутрь и возвращается на кнопку,
 * которая её открыла, — с клавиатуры и VoiceOver иначе теряешься.
 */
export function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  // Родитель («Плеер») перерисовывается четыре раза в секунду, и onClose каждый раз новый:
  // эффект на него перезапускался бы и дёргал фокус, пока шторка открыта
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusable = panel.current?.querySelector<HTMLElement>("button, [href], input, textarea, [tabindex]");
    focusable?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close.current();
    };
    document.addEventListener("keydown", onKey);
    // Прокрутка страницы под шторкой на телефоне уводит экран из-под пальца
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      opener?.focus();
    };
  }, []);

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={panel}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="sheet__head">
          <h2 id={titleId} className="sheet__title">
            {title}
          </h2>
          <button type="button" className="icon-btn icon-btn--quiet" onClick={onClose} aria-label={t("app.close")}>
            <Icon name="close" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
